import React, { useEffect, useState } from 'react';
import { Check, Star, Clock, AlertTriangle, ShieldCheck, ArrowRight, X } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Accordion } from '../components/ui/Accordion';
import { SalesPopup } from '../components/SalesPopup';
import { PixelTracker } from '../components/PixelTracker';
const IMAGES = {
  main: 'https://cdn.magicpatterns.com/uploads/eFsH9MiZ37UKZeqQnDChNj/mocap_pasteleria.png',
  bono1: 'https://cdn.magicpatterns.com/uploads/niNbwmfaz6frkNS3YgnATj/mocap_bono_1.png',
  bono2: 'https://cdn.magicpatterns.com/uploads/qPkY6DaztduWFXBhcYSuDM/mocap_bono_2.png',
  bono3: 'https://cdn.magicpatterns.com/uploads/bSLuWY4XC7CYVqrZRqxSbN/mocap_bono_3.png',
  bono4: 'https://cdn.magicpatterns.com/uploads/v8tmoymo72A91sPabtXqFd/mocap_bono_4.png'
};
const CHECKOUT_URL = 'https://pay.hotmart.com/E104722493I?off=dd73t83h&checkoutMode=10';
export function LandingPage() {
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 14,
    seconds: 42
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return {
            ...prev,
            seconds: prev.seconds - 1
          };
        }
        if (prev.minutes > 0) {
          return {
            ...prev,
            minutes: prev.minutes - 1,
            seconds: 59
          };
        }
        if (prev.hours > 0) {
          return {
            ...prev,
            hours: prev.hours - 1,
            minutes: 59,
            seconds: 59
          };
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (val: number) => val.toString().padStart(2, '0');
  const handleBuyClick = () => {
    window.location.href = CHECKOUT_URL;
  };
  return <div className="min-h-screen bg-white font-sans text-gray-900 pb-20" data-id="element-19">
      <PixelTracker data-id="element-20" />
      <SalesPopup data-id="element-21" />

      {/* 1. Sticky Top Bar - Urgency Banner */}
      <div className="bg-[#cc0000] text-white text-center py-3 px-4 font-bold text-sm md:text-base sticky top-0 z-40 shadow-md" data-id="element-22">
        <div className="flex items-center justify-center gap-2 animate-pulse" data-id="element-23">
          <Clock className="w-5 h-5" aria-hidden="true" data-id="element-24" />
          <span data-id="element-25">
            OFERTA EXPIRA EN: {formatTime(timeLeft.hours)}:
            {formatTime(timeLeft.minutes)}:{formatTime(timeLeft.seconds)} | SOLO
            QUEDAN 5 CUPOS
          </span>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8" data-id="element-26">
        {/* 2. Hero Section */}
        <section className="py-8 md:py-12 text-center" data-id="element-27">
          <p className="text-[#cc0000] font-bold tracking-wider uppercase mb-4 text-sm md:text-base" data-id="element-28">
            🐶 MEGAPACK PASTELERÍA Y NUTRICIÓN CANINA
          </p>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 text-gray-900" data-id="element-29">
            Emprende • Alimenta Mejor •{' '}
            <span className="text-[#ff9900]" data-id="element-30">Genera Ingresos</span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-2xl mx-auto font-medium" data-id="element-31">
            Recetas reales. Nutrición responsable. Un negocio desde casa.
          </p>

          <div className="bg-yellow-50 border-l-4 border-[#ff9900] p-4 mb-8 text-left max-w-2xl mx-auto" data-id="element-32">
            <p className="font-semibold text-gray-800" data-id="element-33">
              Aprendé paso a paso cómo preparar alimentos saludables para perros
              y convertirlo en una fuente de ingresos.
            </p>
          </div>

          <div className="mb-10" data-id="element-34">
            <img src={IMAGES.main} alt="Megapack Pastelería Canina - 6 Tomos + 4 Bonos" className="w-full max-w-2xl mx-auto drop-shadow-2xl hover:scale-105 transition-transform duration-300" data-id="element-35" />
          </div>

          <Button variant="primary" size="xl" onClick={handleBuyClick} className="w-full md:w-auto animate-bounce-slow" aria-label="Acceder al Megapack ahora" data-id="element-36">
            👉 SÍ, QUIERO ACCEDER AHORA
          </Button>

          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500" data-id="element-37">
            <ShieldCheck className="w-4 h-4 text-green-500" aria-hidden="true" data-id="element-38" />
            <span data-id="element-39">Garantía de satisfacción de 7 días</span>
          </div>
        </section>

        {/* 3. Agitation Section - Pain Points */}
        <section className="py-12 border-t border-gray-100" data-id="element-40">
          <div className="prose prose-lg mx-auto text-gray-700 max-w-3xl" data-id="element-41">
            <p className="font-bold text-xl text-gray-900" data-id="element-42">
              Si amas a tu perro o quieres emprender desde casa, dejame hacerte
              una pregunta honesta…
            </p>

            <ul className="space-y-4 list-none pl-0 my-8" role="list" data-id="element-43">
              <li className="flex gap-3 items-start" data-id="element-44">
                <AlertTriangle className="w-6 h-6 text-[#cc0000] flex-shrink-0 mt-1" aria-hidden="true" data-id="element-45" />
                <span data-id="element-46">
                  ¿Te frustra no saber exactamente qué está comiendo tu mascota?
                </span>
              </li>
              <li className="flex gap-3 items-start" data-id="element-47">
                <AlertTriangle className="w-6 h-6 text-[#cc0000] flex-shrink-0 mt-1" aria-hidden="true" data-id="element-48" />
                <span data-id="element-49">
                  ¿Te gustaría generar ingresos extra pero no sabes por dónde
                  empezar?
                </span>
              </li>
              <li className="flex gap-3 items-start" data-id="element-50">
                <AlertTriangle className="w-6 h-6 text-[#cc0000] flex-shrink-0 mt-1" aria-hidden="true" data-id="element-51" />
                <span data-id="element-52">
                  ¿Sientes que podrías hacer algo más… si tuvieras una guía
                  clara y paso a paso?
                </span>
              </li>
            </ul>

            <p data-id="element-53">
              No porque no quieras. No porque no tengas talento. <br data-id="element-54" />
              <span className="font-bold" data-id="element-55">
                Sino porque nadie te enseñó cómo hacerlo correctamente.
              </span>
            </p>

            <p className="bg-gray-100 p-4 rounded-lg border-l-4 border-gray-400 italic my-6" data-id="element-56">
              Y mientras tanto… <br data-id="element-57" />
              Tu perro sigue comiendo productos industriales. <br data-id="element-58" />Y tú sigues
              postergando esa idea de emprender desde casa.
            </p>

            <h3 className="text-2xl font-bold text-[#cc0000] mt-12 mb-6 text-center" data-id="element-59">
              El problema que nadie dice en voz alta
            </h3>

            <p data-id="element-60">Si esto te pasa, no estás sola/o:</p>

            <div className="grid md:grid-cols-2 gap-4 my-6" data-id="element-61">
              <div className="flex items-center gap-2 text-gray-700" data-id="element-62">
                <X className="w-5 h-5 text-red-500 flex-shrink-0" aria-hidden="true" data-id="element-63" />
                <span data-id="element-64">Compras snacks sin saber realmente qué contienen</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700" data-id="element-65">
                <X className="w-5 h-5 text-red-500 flex-shrink-0" aria-hidden="true" data-id="element-66" />
                <span data-id="element-67">Quieres emprender pero no sabes qué vender</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700" data-id="element-68">
                <X className="w-5 h-5 text-red-500 flex-shrink-0" aria-hidden="true" data-id="element-69" />
                <span data-id="element-70">Te preocupa la salud de tu mascota</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700" data-id="element-71">
                <X className="w-5 h-5 text-red-500 flex-shrink-0" aria-hidden="true" data-id="element-72" />
                <span data-id="element-73">No sabes cómo calcular precios</span>
              </div>
              <div className="flex items-center gap-2 text-gray-700" data-id="element-74">
                <X className="w-5 h-5 text-red-500 flex-shrink-0" aria-hidden="true" data-id="element-75" />
                <span data-id="element-76">Tienes miedo de hacerlo mal</span>
              </div>
            </div>

            <p className="text-center font-bold text-lg mt-8" data-id="element-77">
              Y aparece ese pensamiento incómodo: <br data-id="element-78" />
              <span className="text-2xl block mt-2 font-extrabold text-gray-900" data-id="element-79">
                "Si tuviera una guía clara… lo intentaría."
              </span>
            </p>
          </div>
        </section>

        {/* 4. Solution Section */}
        <section className="py-12 bg-green-50 -mx-4 sm:-mx-6 md:-mx-8 px-4 sm:px-6 md:px-8" data-id="element-80">
          <div className="max-w-4xl mx-auto text-center" data-id="element-81">
            <h2 className="text-3xl font-extrabold text-green-700 mb-8" data-id="element-82">
              ✅ La solución
            </h2>
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-83">
              🐶 MEGAPACK PASTELERÍA Y NUTRICIÓN CANINA
            </h3>

            <p className="text-lg text-gray-700 mb-8" data-id="element-84">
              Un sistema completo que te enseña:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto mb-10 text-left" data-id="element-85">
              <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm" data-id="element-86">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" aria-hidden="true" data-id="element-87" />
                <span className="font-semibold text-gray-800" data-id="element-88">
                  Nutrición canina desde cero
                </span>
              </div>
              <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm" data-id="element-89">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" aria-hidden="true" data-id="element-90" />
                <span className="font-semibold text-gray-800" data-id="element-91">
                  Recetas saludables paso a paso
                </span>
              </div>
              <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm" data-id="element-92">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" aria-hidden="true" data-id="element-93" />
                <span className="font-semibold text-gray-800" data-id="element-94">
                  Adaptación para alergias e intolerancias
                </span>
              </div>
              <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm" data-id="element-95">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" aria-hidden="true" data-id="element-96" />
                <span className="font-semibold text-gray-800" data-id="element-97">
                  Técnicas profesionales
                </span>
              </div>
              <div className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm md:col-span-2" data-id="element-98">
                <Check className="w-6 h-6 text-green-500 flex-shrink-0" aria-hidden="true" data-id="element-99" />
                <span className="font-semibold text-gray-800" data-id="element-100">
                  Cómo vender y generar ingresos
                </span>
              </div>
            </div>

            <div className="flex flex-col md:flex-row justify-center items-center gap-6 text-xl font-bold text-gray-800 mb-10" data-id="element-101">
              <div className="flex flex-col items-center" data-id="element-102">
                <span className="text-4xl mb-2" aria-hidden="true" data-id="element-103">
                  📖
                </span>
                <span data-id="element-104">Abrís el Megapack</span>
              </div>
              <ArrowRight className="hidden md:block text-gray-400" aria-hidden="true" data-id="element-105" />
              <div className="flex flex-col items-center" data-id="element-106">
                <span className="text-4xl mb-2" aria-hidden="true" data-id="element-107">
                  👩‍🍳
                </span>
                <span data-id="element-108">Preparás las recetas</span>
              </div>
              <ArrowRight className="hidden md:block text-gray-400" aria-hidden="true" data-id="element-109" />
              <div className="flex flex-col items-center" data-id="element-110">
                <span className="text-4xl mb-2" aria-hidden="true" data-id="element-111">
                  💰
                </span>
                <span data-id="element-112">Comenzás a vender</span>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-4 text-sm font-semibold text-gray-500 uppercase tracking-wide" data-id="element-113">
              <span data-id="element-114">Sin experiencia previa</span>
              <span aria-hidden="true" data-id="element-115">•</span>
              <span data-id="element-116">Sin cursos costosos</span>
              <span aria-hidden="true" data-id="element-117">•</span>
              <span data-id="element-118">Sin salir de casa</span>
            </div>
          </div>
        </section>

        {/* 5. Product Modules - 6 Tomos */}
        <section className="py-16" data-id="element-119">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center text-gray-900 mb-12" data-id="element-120">
            📚 ¿Qué incluye exactamente el Megapack?
          </h2>

          <div className="bg-blue-50 p-6 rounded-xl border-2 border-blue-100 mb-12" data-id="element-121">
            <h3 className="text-2xl font-bold text-blue-800 mb-6 text-center" data-id="element-122">
              📘 6 TOMOS PROFESIONALES
            </h3>
            <div className="space-y-6" data-id="element-123">
              <div className="flex gap-4 items-start" data-id="element-124">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold mt-1" data-id="element-125">
                  1
                </div>
                <div data-id="element-126">
                  <h4 className="font-bold text-lg text-gray-900" data-id="element-127">
                    Tomo 1 – Fundamentos de Nutrición y Salud Canina
                  </h4>
                  <p className="text-gray-600" data-id="element-128">
                    Aprende cómo funciona la alimentación canina y cómo crear
                    recetas equilibradas.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-129">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold mt-1" data-id="element-130">
                  2
                </div>
                <div data-id="element-131">
                  <h4 className="font-bold text-lg text-gray-900" data-id="element-132">
                    Tomo 2 – Panadería y Snacks Caninos
                  </h4>
                  <p className="text-gray-600" data-id="element-133">
                    Galletas, panecillos y productos horneados fáciles y
                    vendibles.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-134">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold mt-1" data-id="element-135">
                  3
                </div>
                <div data-id="element-136">
                  <h4 className="font-bold text-lg text-gray-900" data-id="element-137">
                    Tomo 3 – Snacks Funcionales y Naturales
                  </h4>
                  <p className="text-gray-600" data-id="element-138">
                    Recetas que aportan beneficios digestivos, energéticos y de
                    bienestar.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-139">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold mt-1" data-id="element-140">
                  4
                </div>
                <div data-id="element-141">
                  <h4 className="font-bold text-lg text-gray-900" data-id="element-142">
                    Tomo 4 – Pasteles y Celebraciones Caninas
                  </h4>
                  <p className="text-gray-600" data-id="element-143">
                    Tortas y productos premium para cumpleaños y eventos.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-144">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold mt-1" data-id="element-145">
                  5
                </div>
                <div data-id="element-146">
                  <h4 className="font-bold text-lg text-gray-900" data-id="element-147">
                    Tomo 5 – Helados y Recetas Refrescantes
                  </h4>
                  <p className="text-gray-600" data-id="element-148">
                    Opciones innovadoras para diferenciarte en el mercado.
                  </p>
                </div>
              </div>

              <div className="flex gap-4 items-start" data-id="element-149">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-bold mt-1" data-id="element-150">
                  6
                </div>
                <div data-id="element-151">
                  <h4 className="font-bold text-lg text-gray-900" data-id="element-152">
                    Tomo 6 – Emprendimiento en Pastelería Canina
                  </h4>
                  <p className="text-gray-600" data-id="element-153">
                    Cómo calcular precios, presentar productos y generar
                    ingresos reales.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 6. Bonuses - 4 Bonus Products */}
        <section className="py-12" data-id="element-154">
          <h2 className="text-3xl md:text-4xl font-extrabold text-center text-[#cc0000] mb-4" data-id="element-155">
            🎁 BONOS INCLUIDOS HOY
          </h2>
          <p className="text-center text-gray-500 mb-12 uppercase tracking-widest font-bold" data-id="element-156">
            (Sin costo adicional)
          </p>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-157">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow" data-id="element-158">
              <div className="bg-gray-50 p-4 flex justify-center" data-id="element-159">
                <img src={IMAGES.bono1} alt="Bono 1 - Nutrición y Pastelería Felina" className="h-64 object-contain" data-id="element-160" />
              </div>
              <div className="p-6" data-id="element-161">
                <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded uppercase mb-2 inline-block" data-id="element-162">
                  Gratis Hoy
                </span>
                <h3 className="font-bold text-xl text-gray-900 mb-2" data-id="element-163">
                  Bono #1 – Nutrición y Pastelería Felina
                </h3>
                <p className="text-gray-600" data-id="element-164">
                  Amplía tu mercado aprendiendo también recetas para gatos.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow" data-id="element-165">
              <div className="bg-gray-50 p-4 flex justify-center" data-id="element-166">
                <img src={IMAGES.bono2} alt="Bono 2 - Dieta BARF Paso a Paso" className="h-64 object-contain" data-id="element-167" />
              </div>
              <div className="p-6" data-id="element-168">
                <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded uppercase mb-2 inline-block" data-id="element-169">
                  Gratis Hoy
                </span>
                <h3 className="font-bold text-xl text-gray-900 mb-2" data-id="element-170">
                  Bono #2 – Dieta BARF Paso a Paso
                </h3>
                <p className="text-gray-600" data-id="element-171">
                  Aprende alimentación natural correctamente y sin riesgos.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow" data-id="element-172">
              <div className="bg-gray-50 p-4 flex justify-center" data-id="element-173">
                <img src={IMAGES.bono3} alt="Bono 3 - Guía para Prevenir Enfermedades en Casa" className="h-64 object-contain" data-id="element-174" />
              </div>
              <div className="p-6" data-id="element-175">
                <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded uppercase mb-2 inline-block" data-id="element-176">
                  Gratis Hoy
                </span>
                <h3 className="font-bold text-xl text-gray-900 mb-2" data-id="element-177">
                  Bono #3 – Guía para Prevenir Enfermedades en Casa
                </h3>
                <p className="text-gray-600" data-id="element-178">
                  Cómo cuidar la salud de tu perro desde casa.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow" data-id="element-179">
              <div className="bg-gray-50 p-4 flex justify-center" data-id="element-180">
                <img src={IMAGES.bono4} alt="Bono 4 - Cachorro en Casa" className="h-64 object-contain" data-id="element-181" />
              </div>
              <div className="p-6" data-id="element-182">
                <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded uppercase mb-2 inline-block" data-id="element-183">
                  Gratis Hoy
                </span>
                <h3 className="font-bold text-xl text-gray-900 mb-2" data-id="element-184">
                  Bono #4 – Cachorro en Casa
                </h3>
                <p className="text-gray-600" data-id="element-185">
                  Crianza, alimentación y entrenamiento desde el primer día.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12" data-id="element-186">
            <p className="text-2xl font-bold text-gray-800" data-id="element-187">
              👉 6 Tomos + 4 Bonos en un solo Megapack.
            </p>
          </div>
        </section>

        {/* 7. Before vs After Comparison */}
        <section className="py-12 border-t border-b border-gray-100 bg-gray-50 -mx-4 sm:-mx-6 md:-mx-8 px-4 sm:px-6 md:px-8" data-id="element-188">
          <div className="max-w-4xl mx-auto" data-id="element-189">
            <h2 className="text-3xl font-extrabold text-center mb-10" data-id="element-190">
              🔄 Antes vs Después
            </h2>

            <div className="grid md:grid-cols-2 gap-8" data-id="element-191">
              <div className="bg-white p-8 rounded-xl shadow-sm border-t-4 border-red-500" data-id="element-192">
                <h3 className="text-xl font-bold text-red-600 mb-6 flex items-center gap-2" data-id="element-193">
                  <X className="w-6 h-6" aria-hidden="true" data-id="element-194" /> Antes del
                  Megapack
                </h3>
                <ul className="space-y-4" role="list" data-id="element-195">
                  <li className="flex items-start gap-3 text-gray-600" data-id="element-196">
                    <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-197" />
                    <span data-id="element-198">No sabes qué ingredientes usar</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-600" data-id="element-199">
                    <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-200" />
                    <span data-id="element-201">Compras snacks industriales</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-600" data-id="element-202">
                    <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-203" />
                    <span data-id="element-204">No tienes ingresos extra</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-600" data-id="element-205">
                    <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-206" />
                    <span data-id="element-207">Tienes dudas sobre la nutrición</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-600" data-id="element-208">
                    <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-209" />
                    <span data-id="element-210">Postergas la idea de emprender</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-8 rounded-xl shadow-lg border-t-4 border-green-500 transform md:-translate-y-4" data-id="element-211">
                <h3 className="text-xl font-bold text-green-600 mb-6 flex items-center gap-2" data-id="element-212">
                  <Check className="w-6 h-6" aria-hidden="true" data-id="element-213" /> Después del
                  Megapack
                </h3>
                <ul className="space-y-4" role="list" data-id="element-214">
                  <li className="flex items-start gap-3 text-gray-800 font-medium" data-id="element-215">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-216" />
                    <span data-id="element-217">Preparas alimentos saludables</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-800 font-medium" data-id="element-218">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-219" />
                    <span data-id="element-220">Sabes exactamente qué estás usando</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-800 font-medium" data-id="element-221">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-222" />
                    <span data-id="element-223">Generas ingresos desde casa</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-800 font-medium" data-id="element-224">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-225" />
                    <span data-id="element-226">Te diferencias con productos premium</span>
                  </li>
                  <li className="flex items-start gap-3 text-gray-800 font-medium" data-id="element-227">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-228" />
                    <span data-id="element-229">Confianza para vender</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 8. Stack/Offer Summary & Pricing */}
        <section className="py-16" data-id="element-230">
          <div className="bg-gray-900 text-white rounded-2xl p-8 md:p-12 shadow-2xl" data-id="element-231">
            <h2 className="text-3xl md:text-4xl font-extrabold text-center text-yellow-400 mb-10" data-id="element-232">
              🎁 ¿Qué vas a recibir hoy?
            </h2>

            <div className="grid md:grid-cols-2 gap-x-12 gap-y-4 max-w-2xl mx-auto" data-id="element-233">
              <div className="flex items-center gap-3 border-b border-gray-700 pb-2" data-id="element-234">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" aria-hidden="true" data-id="element-235" />
                <span className="text-lg" data-id="element-236">6 libros principales</span>
              </div>
              <div className="flex items-center gap-3 border-b border-gray-700 pb-2" data-id="element-237">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" aria-hidden="true" data-id="element-238" />
                <span className="text-lg" data-id="element-239">4 bonos exclusivos</span>
              </div>
              <div className="flex items-center gap-3 border-b border-gray-700 pb-2" data-id="element-240">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" aria-hidden="true" data-id="element-241" />
                <span className="text-lg" data-id="element-242">Recetas paso a paso</span>
              </div>
              <div className="flex items-center gap-3 border-b border-gray-700 pb-2" data-id="element-243">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" aria-hidden="true" data-id="element-244" />
                <span className="text-lg" data-id="element-245">Formación en nutrición</span>
              </div>
              <div className="flex items-center gap-3 border-b border-gray-700 pb-2" data-id="element-246">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" aria-hidden="true" data-id="element-247" />
                <span className="text-lg" data-id="element-248">Formación en emprendimiento</span>
              </div>
              <div className="flex items-center gap-3 border-b border-gray-700 pb-2" data-id="element-249">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" aria-hidden="true" data-id="element-250" />
                <span className="text-lg" data-id="element-251">
                  Formato PDF (celular, tablet, PC)
                </span>
              </div>
              <div className="flex items-center gap-3 border-b border-gray-700 pb-2" data-id="element-252">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" aria-hidden="true" data-id="element-253" />
                <span className="text-lg" data-id="element-254">Podés imprimirlo</span>
              </div>
              <div className="flex items-center gap-3 border-b border-gray-700 pb-2" data-id="element-255">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" aria-hidden="true" data-id="element-256" />
                <span className="text-lg" data-id="element-257">Acceso inmediato</span>
              </div>
              <div className="flex items-center gap-3 border-b border-gray-700 pb-2 md:col-span-2" data-id="element-258">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0" aria-hidden="true" data-id="element-259" />
                <span className="text-lg" data-id="element-260">Un solo pago</span>
              </div>
            </div>

            {/* Pricing */}
            <div className="mt-12 text-center bg-white text-gray-900 rounded-xl p-8 max-w-lg mx-auto" data-id="element-261">
              <p className="text-gray-500 text-lg mb-2" data-id="element-262">
                Precio regular del contenido completo:
              </p>
              <p className="text-2xl text-red-500 line-through font-bold mb-6" data-id="element-263">
                USD $45
              </p>

              <div className="bg-yellow-100 inline-block px-6 py-2 rounded-full mb-4" data-id="element-264">
                <span className="text-yellow-800 font-bold tracking-wider" data-id="element-265">
                  🔥 HOY SOLO
                </span>
              </div>

              <p className="text-6xl md:text-7xl font-extrabold text-gray-900 mb-4" data-id="element-266">
                USD $9.99
              </p>

              <div className="flex flex-col gap-1 text-sm text-gray-500 mb-8" data-id="element-267">
                <span data-id="element-268">Pagalo en tu moneda local</span>
                <span data-id="element-269">Descarga digital inmediata</span>
              </div>

              <Button variant="primary" size="xl" fullWidth onClick={handleBuyClick} className="animate-pulse" aria-label="Comprar el Megapack ahora" data-id="element-270">
                👉 QUIERO EL MEGAPACK AHORA
              </Button>
            </div>
          </div>
        </section>

        {/* 9. Guarantee */}
        <section className="py-12 max-w-2xl mx-auto text-center" data-id="element-271">
          <div className="border-4 border-dashed border-gray-300 rounded-xl p-8" data-id="element-272">
            <ShieldCheck className="w-16 h-16 text-green-500 mx-auto mb-4" aria-hidden="true" data-id="element-273" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-274">
              🔒 Garantía sin riesgo
            </h2>
            <p className="text-lg text-gray-700 mb-4" data-id="element-275">
              Probalo durante 7 días. Si no es para vos, te devolvemos el 100%
              del dinero.
            </p>
            <p className="font-bold text-gray-900" data-id="element-276">
              Sin preguntas. Sin letra chica.
            </p>
          </div>
        </section>

        {/* 10. Testimonials */}
        <section className="py-12 bg-gray-50 -mx-4 sm:-mx-6 md:-mx-8 px-4 sm:px-6 md:px-8" data-id="element-277">
          <div className="max-w-4xl mx-auto" data-id="element-278">
            <h2 className="text-3xl font-extrabold text-center mb-12" data-id="element-279">
              💬 Lo que dicen nuestras alumnas
            </h2>

            <div className="grid md:grid-cols-2 gap-6" data-id="element-280">
              <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-281">
                <div className="flex text-yellow-400 mb-3" aria-label="5 estrellas" data-id="element-282">
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-283" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-284" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-285" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-286" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-287" />
                </div>
                <p className="text-gray-700 italic mb-4" data-id="element-288">
                  "Empecé preparando para mi perro… ahora vendo todas las
                  semanas."
                </p>
                <p className="font-bold text-gray-900 text-sm" data-id="element-289">
                  — Laura, emprendedora
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-290">
                <div className="flex text-yellow-400 mb-3" aria-label="5 estrellas" data-id="element-291">
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-292" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-293" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-294" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-295" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-296" />
                </div>
                <p className="text-gray-700 italic mb-4" data-id="element-297">
                  "No sabía nada de nutrición. Ahora preparo todo en casa con
                  seguridad."
                </p>
                <p className="font-bold text-gray-900 text-sm" data-id="element-298">
                  — Mariana, mamá perruna
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-299">
                <div className="flex text-yellow-400 mb-3" aria-label="5 estrellas" data-id="element-300">
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-301" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-302" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-303" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-304" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-305" />
                </div>
                <p className="text-gray-700 italic mb-4" data-id="element-306">
                  "En 2 semanas recuperé la inversión."
                </p>
                <p className="font-bold text-gray-900 text-sm" data-id="element-307">
                  — Carla, pastelería casera
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm" data-id="element-308">
                <div className="flex text-yellow-400 mb-3" aria-label="5 estrellas" data-id="element-309">
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-310" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-311" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-312" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-313" />
                  <Star className="w-4 h-4 fill-current" aria-hidden="true" data-id="element-314" />
                </div>
                <p className="text-gray-700 italic mb-4" data-id="element-315">
                  "No es solo un recetario, es un negocio."
                </p>
                <p className="font-bold text-gray-900 text-sm" data-id="element-316">— Andrea</p>
              </div>
            </div>
          </div>
        </section>

        {/* 11. FAQ */}
        <section className="py-16 max-w-3xl mx-auto" data-id="element-317">
          <h2 className="text-3xl font-extrabold text-center mb-10" data-id="element-318">
            ❓ Preguntas frecuentes
          </h2>
          <Accordion items={[{
          title: '¿Es material digital?',
          content: 'Sí, se entrega en PDF y llega al instante a tu correo electrónico.'
        }, {
          title: '¿Necesito experiencia previa?',
          content: 'No, está diseñado para principiantes con instrucciones paso a paso.'
        }, {
          title: '¿Puedo vender lo que aprenda?',
          content: 'Sí, el Megapack incluye un módulo completo de emprendimiento para que inicies tu negocio.'
        }, {
          title: '¿Sirve si mi perro tiene alergias?',
          content: 'Sí, incluye adaptación de recetas para perros con alergias e intolerancias alimentarias.'
        }]} data-id="element-319" />
        </section>

        {/* 12. Final CTA */}
        <section className="py-16 text-center" data-id="element-320">
          <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-321">
            🐶 La salud de tu perro no se improvisa
          </h2>
          <p className="text-xl text-gray-600 mb-8" data-id="element-322">
            Y tu emprendimiento tampoco.
          </p>

          <div className="bg-gray-100 inline-block px-6 py-3 rounded-lg mb-8" data-id="element-323">
            <p className="font-semibold text-gray-800" data-id="element-324">
              No necesitás más tiempo. Necesitás el sistema correcto.
            </p>
          </div>

          <div className="max-w-md mx-auto" data-id="element-325">
            <Button variant="primary" size="xl" fullWidth onClick={handleBuyClick} aria-label="Acceder al Megapack ahora" data-id="element-326">
              👉 Accedé ahora al MEGAPACK
            </Button>
          </div>
        </section>

        {/* 13. Footer */}
        <footer className="py-8 text-center text-gray-500 text-sm border-t border-gray-200" data-id="element-327">
          <p className="mb-2" data-id="element-328">
            © 2026 DescargaTop – Megapack Pastelería y Nutrición Canina
          </p>
          <p data-id="element-329">
            Todos los derechos reservados | Privacidad · Términos y Condiciones
          </p>
        </footer>
      </div>
    </div>;
}